import { ConfigProvider, DatePicker } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import FullPageLoader from "../../../core/common/loader/FullPageLoader";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { getPresignedUrl } from "../../../core/data/redux/awsBucketSlice";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import {
  clearCurrentRequest,
  createNOCOutjobRequest,
  deleteFileFromAWS,
  fetchNOCOutjobRequests,
  setCurrentRequest,
  uploadFileToAWS,
} from "../../../core/data/redux/nocOutjobSlice";
import Api from "../../../core/data/redux/utils/apiCall";
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_MB,
  validateFileSize,
  validateFileType,
  formatFileSize,
} from "../../../utils/fileUtils";
import { all_routes } from "../../router/all_routes";
import { useT } from "../../../core/i18n";

// Yup validation schema
const validationSchema = Yup.object().shape({
  // Conditional validation for place and year of training abroad
  // Only validate if trainedAbroadByDM is "Y" (Yes)
  placeAbroadTrain: Yup.string().when("trainedAbroadByDM", {
    is: "Y",
    then: (schema) =>
      schema
        .required(
          "Place of Training Abroad is required when trained abroad by DMRC"
        )
        .max(50, "Place of Training Abroad must be 50 characters or less"),
    otherwise: (schema) =>
      schema
        .nullable()
        .optional()
        .max(50, "Place of Training Abroad must be 50 characters or less"),
  }),

  yearOfAbroadTrain: Yup.string().when("trainedAbroadByDM", {
    is: "Y",
    then: (schema) =>
      schema
        .required("Year of Training is required when trained abroad by DMRC")
        .matches(/^\d{4}$/, "Year must be a valid 4-digit year"),
    otherwise: (schema) =>
      schema
        .nullable()
        .optional()
        .transform((value) => (!value ? null : value))
        .test(
          "is-valid-year",
          "Year must be a valid 4-digit year if provided",
          (val) => !val || /^\d{4}$/.test(val)
        ),
  }),

  placeofPosting: Yup.string()
    .required("Place of Posting is required")
    .max(50, "Place of Posting must be 50 characters or less"),

  postAppliedFor: Yup.string()
    .required("Name of Post applied for is required")
    .max(50, "Post Applied For must be 50 characters or less"),

  newspaperWebsite: Yup.string()
    .required("Newspaper/website is required")
    .max(255, "Newspaper/website must be 255 characters or less"),

  advertisementNo: Yup.string()
    .required("Advertisement No is required")
    .max(50, "Advertisement No must be 50 characters or less"),

  organizationApplied: Yup.string()
    .required("Name of the organization is required")
    .max(50, "Organization name must be 50 characters or less"),

  // Education fields validation
  courseName: Yup.string().max(50, "Course name must be 50 characters or less"),

  courseAffiliated: Yup.string().max(
    50,
    "Course affiliated must be 50 characters or less"
  ),

  // Required fields validation
  whetherProbation: Yup.string().required(
    "Please select whether probation is confirmed in DMRC"
  ),

  whetherProbationConfirmed: Yup.string().required(
    "Please select whether probation confirmation applies"
  ),

  trainedAbroadByDM: Yup.string().required(
    "Please select whether trained abroad by DMRC"
  ),

  appNoInYear: Yup.string().required(
    "Please select application number in year"
  ),

  natureOfOutside: Yup.string().required("Please select nature of outside job"),

  advertisementDate: Yup.string().required("Advertisement date is required"),

  lastDateReceipt: Yup.string().required("Last date of receipt is required"),

  dandARPendingOrNot: Yup.string().nullable(),
  vigilancePending: Yup.string().nullable(),
});

// Education details validation schema
const educationValidationSchema = Yup.object().shape({
  courseName: Yup.string()
    .required("Course name is required")
    .max(50, "Course name must be 50 characters or less"),

  dateOfAdmission: Yup.string().required("Date of admission is required"),

  dateOfPassing: Yup.string().required("Date of passing is required"),

  whenQualificationAcquired: Yup.string().required("Please select when qualification acquired"),

  courseAffiliated: Yup.string().required(
    "Please select if course is affiliated by UGC/AICTE"
  ),
});

interface NOCOutsideJobCreateFormProps { }

const NOCOutsideJobCreateForm = (props: NOCOutsideJobCreateFormProps) => {
  const routes = all_routes;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<any>();
  const { t, tBoth } = useT();
  const employeesState = useSelector((state: any) => state.employeesState);
  const { error, currentRequest, requests } = useSelector(
    (state: any) => state.nocOutjobState
  );

  // Check if we're in edit mode
  const isEditMode = Boolean(id);
  const editId = id ? parseInt(id, 10) : null;

  // Get current user data
  const user = employeesState?.data?.data;

  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    dateOfJoining: "",
    employmentStatus: "Direct recruit",
    department: "",
    contactNumber: "",
    email: "",
    dateOfBirth: "",
    age: "",
    lwp: "",
    category: "",
    organization: "",
    supervisorId: "",
    whetherProbation: "",
    whetherProbationConfirmed: "",
    probationConfirmed: "",
    trainedAbroadByDM: "",
    placeAbroadTrain: "",
    yearOfAbroadTrain: "",
    notApplicableForDeputation: "",
    appNoInYear: "",
    placeofPosting: "",
    natureOfOutside: "",
    natureDetails: {
      natureId: "",
      natureTitle: "",
    },
    postAppliedFor: "",
    newspaperWebsite: "",
    advertisementNo: "",
    advertisementDate: "",
    organizationApplied: "",
    lastDateReceipt: "",
    uploadAdvertising: [] as File[],
    organizationName: "",
    lastDateReceiptSecond: "",
    educationDetails: null as File | null,
    dandARPendingOrNot: "",
    vigilancePending: "",
    dandARPenaltyEffectiveDate: "",
    vigilancePenaltyEffectiveDate: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [showProbationAlert, setShowProbationAlert] = useState(false);
  const [showEducationModal, setShowEducationModal] = useState(false);
  const [showDeclarationModal, setShowDeclarationModal] = useState(false);
  const [showOverviewModal, setShowOverviewModal] = useState(!isEditMode); // Show overview modal only for create mode, not for edit mode
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Processing...");
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [responseType, setResponseType] = useState<"success" | "error">(
    "success"
  );

  // Form validation states
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [educationValidationErrors, setEducationValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [enableRealtimeValidation, setEnableRealtimeValidation] =
    useState(false);

  // State for saved education records
  const [savedEducationRecords, setSavedEducationRecords] = useState<
    Array<{
      id: string;
      courseName: string;
      dateOfAdmission: string;
      dateOfPassing: string;
      whenQualificationAcquired: string;
      courseAffiliated: string;
      uploadedFiles: Array<{
        file: File;
        key: string;
        url: string;
        type: "noc" | "education" | "advertising";
      }>;
      createdAt: string;
    }>
  >([]);
  const [outsideJobNatureOptions, setOutsideJobNatureOptions] = useState<
    Array<{
      sap_master_natures_id: number;
      sap_master_natures_natureId: string;
      sap_master_natures_natureTitle: string;
    }>
  >([]);
  const [isLoadingJobNature, setIsLoadingJobNature] = useState(true);

  const [educationDetails, setEducationDetails] = useState({
    courseName: "",
    dateOfAdmission: "",
    dateOfPassing: "",
    whenQualificationAcquired: "",
    courseAffiliated: "",
    nocAdditionFiles: [] as File[],
    educationQualificationFiles: [] as File[],
    advertisingDocumentsFiles: [] as File[],
  });

  const [declarationChecks, setDeclarationChecks] = useState({
    informationTrue: false,
    eligibilityCriteria: false,
  });

  // File tracking state for uploaded files
  const [uploadedFiles, setUploadedFiles] = useState<
    Array<{
      file: File;
      key: string;
      url: string;
      uploading?: boolean;
      error?: string;
    }>
  >([]);

  const [uploadedEducationFiles, setUploadedEducationFiles] = useState<
    Array<{
      file: File;
      key: string;
      url: string;
      uploading?: boolean;
      error?: string;
    }>
  >([]);

  // Ref for the advertisement file input
  const advertisementFileInputRef = useRef<HTMLInputElement>(null);

  // Refs for education modal file inputs
  const nocAdditionFileRef = useRef<HTMLInputElement>(null);
  const educationQualificationFileRef = useRef<HTMLInputElement>(null);
  const advertisingDocumentsFileRef = useRef<HTMLInputElement>(null);

  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});
  const registerFieldRef = (fieldName: string) => (element: HTMLElement | null) => {
    if (element) {
      fieldRefs.current[fieldName] = element;
    } else {
      delete fieldRefs.current[fieldName];
    }
  };

  const scrollToField = (fieldName?: string) => {
    if (!fieldName) {
      return;
    }
    const element = fieldRefs.current[fieldName];
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusable = element.querySelector<HTMLElement>(
        "input, select, textarea, button, [tabindex]"
      );
      if (focusable && typeof focusable.focus === "function") {
        focusable.focus();
      }
    }
  };

  // Validation functions
  const validateField = async (fieldName: string, value: string) => {
    try {
      await validationSchema.validateAt(fieldName, {
        ...formData,
        [fieldName]: value,
      });
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    } catch (error: any) {
      setValidationErrors((prev) => ({
        ...prev,
        [fieldName]: error.message,
      }));
    }
  };

  const validateEducationField = async (fieldName: string, value: string) => {
    try {
      await educationValidationSchema.validateAt(fieldName, {
        [fieldName]: value,
      });
      setEducationValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    } catch (error: any) {
      setEducationValidationErrors((prev) => ({
        ...prev,
        [fieldName]: error.message,
      }));
    }
  };

  const validateForm = async (): Promise<{
    isValid: boolean;
    firstErrorField?: string;
    errors?: { [key: string]: string };
  }> => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setValidationErrors({});
      return { isValid: true };
    } catch (error: any) {
      const errors: { [key: string]: string } = {};
      error.inner.forEach((err: any) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      setValidationErrors(errors);
      const firstErrorField = error.inner?.[0]?.path || Object.keys(errors)[0];
      return { isValid: false, firstErrorField, errors };
    }
  };

  const validateEducationDetails = async () => {
    try {
      await educationValidationSchema.validate(educationDetails, {
        abortEarly: false,
      });
      setEducationValidationErrors({});
      return true;
    } catch (error: any) {
      const errors: { [key: string]: string } = {};
      error.inner.forEach((err: any) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      setEducationValidationErrors(errors);
      return false;
    }
  };

  // Fetch outside job nature options
  const fetchOutsideJobNatureOptions = async () => {
    try {
      setIsLoadingJobNature(true);
      const response = await Api.post(API_URLS.NOC.OUTSIDE_JOB_NATURE, {});

      // Handle the specific API response structure
      if (
        response.data &&
        response.data.data &&
        response.data.data.data &&
        Array.isArray(response.data.data.data)
      ) {
        setOutsideJobNatureOptions(response.data.data.data);
      } else if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        setOutsideJobNatureOptions(response.data.data);
      } else {
        console.error("Invalid API response format:", response.data);
        // Fallback to default options if API fails
        setOutsideJobNatureOptions([
          {
            sap_master_natures_id: 1,
            sap_master_natures_natureId: "1",
            sap_master_natures_natureTitle:
              "Govt Sector Central (Presidency/Government departments)",
          },
          {
            sap_master_natures_id: 2,
            sap_master_natures_natureId: "2",
            sap_master_natures_natureTitle:
              "Govt Sector State (State Government Departments)",
          },
          {
            sap_master_natures_id: 3,
            sap_master_natures_natureId: "3",
            sap_master_natures_natureTitle:
              "Semi Govt Sector (PSU, University, Municipal Corporation)",
          },
          {
            sap_master_natures_id: 4,
            sap_master_natures_natureId: "4",
            sap_master_natures_natureTitle: "Private Sector",
          },
        ]);
      }
    } catch (error) {
      console.error("Failed to fetch outside job nature options:", error);
      // Fallback to default options if API fails
      setOutsideJobNatureOptions([
        {
          sap_master_natures_id: 1,
          sap_master_natures_natureId: "1",
          sap_master_natures_natureTitle:
            "Govt Sector Central (Presidency/Government departments)",
        },
        {
          sap_master_natures_id: 2,
          sap_master_natures_natureId: "2",
          sap_master_natures_natureTitle:
            "Govt Sector State (State Government Departments)",
        },
        {
          sap_master_natures_id: 3,
          sap_master_natures_natureId: "3",
          sap_master_natures_natureTitle:
            "Semi Govt Sector (PSU, University, Municipal Corporation)",
        },
        {
          sap_master_natures_id: 4,
          sap_master_natures_natureId: "4",
          sap_master_natures_natureTitle: "Private Sector",
        },
      ]);
    } finally {
      setIsLoadingJobNature(false);
    }
  };

  // Remove education record
  const removeEducationRecord = async (recordId: string) => {
    const recordToRemove = savedEducationRecords.find((r) => r.id === recordId);

    if (!recordToRemove) return;

    if (
      !window.confirm(
        "Are you sure you want to remove this education record? This will also delete all associated files from AWS."
      )
    ) {
      return;
    }

    // Delete all associated files from AWS
    for (const fileData of recordToRemove.uploadedFiles) {
      if (fileData.key) {
        try {
          await dispatch(deleteFileFromAWS({ key: fileData.key })).unwrap();
        } catch (error) {
          console.error("Failed to delete file from AWS:", error);
        }
      }
    }

    // Remove from saved records
    setSavedEducationRecords((prev) => prev.filter((r) => r.id !== recordId));
  };
  // Fetch user profile and outside job nature options on component mount
  useEffect(() => {
    dispatch(getMyProfile({}));
    fetchOutsideJobNatureOptions();

    // Set a timeout to stop loading if API takes too long
    const timeout = setTimeout(() => {
      setIsLoadingProfile(false);
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, [dispatch]);

  // Update form data when user data is available
  useEffect(() => {
    if (user?.details) {
      const userDetails = user.details;
      const calculatedAge = userDetails.age || user.age || calculateAge(userDetails.birthdate || "");

      setFormData((prev) => ({
        ...prev,
        name: `${userDetails.firstname || ""} ${userDetails.lsastname || ""
          }`.trim(),
        // Use sap_department_designation_wings data if available, otherwise use raw IDs
        designation:
          userDetails.sap_department_designation_wings?.designation_name ||
          userDetails.designation_details?.designationName ||
          `Designation ID: ${userDetails.designationID || ""}`,
        dateOfJoining: userDetails.date_of_joining
          ? moment(userDetails.date_of_joining).format("DD-MM-YYYY")
          : "",
        employmentStatus:
          userDetails.employmentType || user.employmentType || user.status,
        department:
          userDetails.sap_department_designation_wings?.department_name ||
          userDetails.department_details?.departmentName ||
          `Department ID: ${userDetails.department || ""}`,
        contactNumber: userDetails.phone || "",
        email: userDetails.email || "",
        dateOfBirth: userDetails.birthdate
          ? moment(userDetails.birthdate).format("DD-MM-YYYY")
          : "",
        age: userDetails.age || user.age || calculatedAge,
        // Category and LWP fields don't exist in current employee profile schema
        // Using fallback values until these fields are added to the database
        category: userDetails.category || userDetails.caste || "",
        lwp: userDetails.lwp || "00000", // Default LWP value
      }));

      setIsLoadingProfile(false);
    } else if (employeesState?.loading === "fulfilled" && !user?.details) {
      // If API call completed but no user data, stop loading anyway
      setIsLoadingProfile(false);
    }
  }, [user, employeesState?.loading]);

  // Calculate age on component mount if not provided
  useEffect(() => {
    if (!formData.age && formData.dateOfBirth) {
      const calculatedAge = calculateAge(formData.dateOfBirth);
      if (calculatedAge) {
        setFormData((prev) => ({ ...prev, age: calculatedAge }));
      }
    }
  }, [formData.dateOfBirth, formData.age]);

  // Set current request from existing data when in edit mode
  useEffect(() => {
    if (isEditMode && editId) {
      // Look for the request in the existing requests array
      dispatch(setCurrentRequest(editId));

      // If no data in store and no current request, we might need to fetch the list
      if (requests.length === 0 && !currentRequest) {
        // Fetch the list to populate the store
        dispatch(
          fetchNOCOutjobRequests({
            page: 1,
            limit: 100, // Get more records to increase chance of finding the one we need
            orderColumn: "created_at",
            orderBy: "DESC",
          })
        );
      }
    }

    // Clear current request when component unmounts or switches modes
    return () => {
      if (isEditMode) {
        dispatch(clearCurrentRequest());
      }
    };
  }, [isEditMode, editId, dispatch, requests.length]);

  // Populate form when current request data is available (edit mode)
  useEffect(() => {
    if (isEditMode && currentRequest) {
      setFormData((prev) => ({
        ...prev,
        id: currentRequest.id,
        postAppliedFor: currentRequest.postAppliedFor || "",
        organizationApplied:
          currentRequest.organisationInWhich ||
          currentRequest.organisation ||
          "",
        natureOfOutside:
          currentRequest.natureOfOutside ||
          currentRequest.natureDetails?.natureId ||
          currentRequest.natureOfOutsideJob ||
          "",
        newspaperWebsite: currentRequest.newspaperWebsite || "",
        advertisementNo: currentRequest.advertisementNo || "",
        advertisementDate: currentRequest.advertisementDate
          ? moment(currentRequest.advertisementDate).format("YYYY-MM-DD")
          : "",
        lastDateReceipt: currentRequest.lastDateOfApplica
          ? moment(currentRequest.lastDateOfApplica).format("YYYY-MM-DD")
          : "",
        placeofPosting: currentRequest.placeofPosting || "",
        appNoInYear: currentRequest.appNoInYear || "",
        whetherProbation: currentRequest.whetherProbation || "",
        whetherProbationConfirmed:
          currentRequest.whetherProbationConfirmed ||
          currentRequest.threeyearsRegularServ ||
          "",
        trainedAbroadByDM: currentRequest.trainedAbroadByDM || "",
        placeAbroadTrain: currentRequest.placeAbroadTrain || "",
        yearOfAbroadTrain: currentRequest.yearOfAbroadTrain || "",
        dandARPendingOrNot:
          currentRequest.dandARPendingOrNot ||
          currentRequest.dandARStatus ||
          "",
        vigilancePending: currentRequest.vigilancePending || "",
        dandARPenaltyEffectiveDate: currentRequest.dandARPenaltyEffective
          ? moment(currentRequest.dandARPenaltyEffective).format("YYYY-MM-DD")
          : "",
        vigilancePenaltyEffectiveDate: currentRequest.vigilancePenaltyEffective
          ? moment(currentRequest.vigilancePenaltyEffective).format(
            "YYYY-MM-DD"
          )
          : "",
      }));

      // Populate education records if available
      if (currentRequest.education && Array.isArray(currentRequest.education)) {
        const educationRecords = currentRequest.education.map(
          (edu: any, index: number) => ({
            id: edu.id?.toString() || Date.now().toString() + index,
            courseName: edu.courseName || "",
            dateOfAdmission: edu.courseDateOfAdmission
              ? moment(edu.courseDateOfAdmission).format("YYYY-MM-DD")
              : "",
            dateOfPassing: edu.courseDateOfPassing
              ? moment(edu.courseDateOfPassing).format("YYYY-MM-DD")
              : "",
            whenQualificationAcquired: edu.qualificationAcquir || "",
            courseAffiliated: edu.courceAfflitaed || "",
            uploadedFiles: edu.edu_documents
              ? edu.edu_documents.map((doc: any) => ({
                file: { name: doc.fileName } as File,
                key: doc.referenceNumber || "",
                url: doc.content || "", // Assuming URL is in content field
                type: doc.typeOfAttachedDoc || ("education" as const),
              }))
              : [],
            createdAt: edu.created_at || new Date().toISOString(),
          })
        );
        setSavedEducationRecords(educationRecords);
      }

      // Populate advertisement documents if available
      if (currentRequest.documents && Array.isArray(currentRequest.documents)) {
        // Filter for advertisement documents only
        const advertisementDocs = currentRequest.documents.filter(
          (doc: any) => doc.typeOfAttachedDoc === "advertising"
        );

        if (advertisementDocs.length > 0) {
          const advertisementFiles = advertisementDocs.map((doc: any) => ({
            file: { name: doc.fileName } as File,
            key: doc.referenceNumber || "",
            url: doc.content || "", // Assuming URL is in content field
            uploading: false,
          }));
          setUploadedFiles(advertisementFiles);
        }
      }
    }
  }, [isEditMode, currentRequest]);

  const calculateAge = (dateOfBirth: string) => {
    if (!dateOfBirth) return "";

    // Handle both DD-MM-YYYY format and ISO date format
    let birthDate;
    if (dateOfBirth.includes("-") && dateOfBirth.split("-").length === 3) {
      // DD-MM-YYYY format
      const parts = dateOfBirth.split("-");
      if (parts[0].length === 2) {
        // DD-MM-YYYY format
        birthDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`); // Convert to YYYY-MM-DD
      } else {
        // YYYY-MM-DD format
        birthDate = new Date(dateOfBirth);
      }
    } else if (dateOfBirth.includes(".")) {
      // DD.MM.YYYY format (legacy support)
      birthDate = new Date(dateOfBirth.split(".").reverse().join("-")); // Convert to YYYY-MM-DD
    } else {
      // ISO date format
      birthDate = new Date(dateOfBirth);
    }

    const today = new Date();

    // Calculate years
    let years = today.getFullYear() - birthDate.getFullYear();

    // Calculate months
    let months = today.getMonth() - birthDate.getMonth();

    // Calculate days
    let days = today.getDate() - birthDate.getDate();

    // Adjust for negative days
    if (days < 0) {
      months--;
      const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += lastMonth.getDate();
    }

    // Adjust for negative months
    if (months < 0) {
      years--;
      months += 12;
    }

    return `${years}y, ${months}m, ${days}d`;
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    let updatedData = { [name]: value };

    // Auto-calculate age when date of birth changes
    if (name === "dateOfBirth") {
      updatedData.age = calculateAge(value);
    }

    setFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));

    // Validate the field only if realtime validation is enabled
    if (enableRealtimeValidation) {
      validateField(name, updatedData[name]);
    }
  };

  const handleRadioChange = (name: string, value: string) => {
    // Show popup if probation is not confirmed
    if (name === "whetherProbation" && value === "N") {
      setShowProbationAlert(true);
      return; // Don't update the form data yet
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (enableRealtimeValidation) {
      validateField(name, value);
    }
  };

  // Utility function to handle date fields properly for backend compatibility
  const convertDateForBackend = (
    value: string | null | undefined
  ): string | undefined => {
    if (!value || value.trim() === "") {
      return undefined; // Don't send empty dates to backend
    }
    return value;
  };

  const convertQualificationTimingToCode = (value: string): string => {
    switch (value) {
      case "After joining":
        return "A";
      case "Before joining":
        return "B";
      default:
        return "A"; // Default to "A" if value is unclear
    }
  };

  // Utility function to convert document types to backend codes (Bug 422: maxlength=1)
  const convertDocTypeToCode = (type: string): string => {
    switch (type.toLowerCase()) {
      case "advertising":
        return "A";
      case "education":
        return "E";
      case "noc":
      case "addition":
        return "N";
      default:
        // If it's already a single char or and unknown type, return as is (but truncate to 1 char just in case)
        return type.length > 1 ? type.charAt(0).toUpperCase() : type;
    }
  };

  const handleSaveDraft = async () => {
    // Validate required fields for draft
    if (!formData.postAppliedFor || !formData.postAppliedFor.trim()) {
      toast.error("Post Applied For is required to save as draft");
      return;
    }

    if (!formData.organizationApplied || !formData.organizationApplied.trim()) {
      toast.error("Organisation is required to save as draft");
      return;
    }

    setIsSubmitting(true);
    setShowLoadingModal(true);
    setLoadingMessage(isEditMode ? "Updating draft..." : "Saving draft...");

    try {
      // Check if all uploaded files have been processed successfully
      // const failedUploads = uploadedFiles.filter((f) => f.error || f.uploading);
      // if (failedUploads.length > 0) {
      //   toast.error(
      //     "Please wait for all files to upload successfully or remove failed uploads before saving draft."
      //   );
      //   return;
      // }

      // Prepare documents array from uploaded files
      // In edit mode, preserve existing documents and add new ones
      let documents: Array<{
        fileName: string;
        typeOfAttachedDoc: string;
      }> = [];

      if (isEditMode && currentRequest?.documents) {
        // Keep existing documents (convert to the format expected by backend)
        const existingDocs = currentRequest.documents
          .filter((doc: any) => doc.typeOfAttachedDoc === "advertising")
          .map((doc: any) => ({
            fileName: doc.fileName,
            typeOfAttachedDoc: doc.typeOfAttachedDoc,
            // Don't include ID - backend doesn't expect it
          }));
        documents = [...existingDocs];
      }

      // Add any newly uploaded files
      const newDocs = uploadedFiles
        .filter((f) => f.key && f.url) // Filter valid uploaded files
        .map((f) => ({ fileName: f.key, typeOfAttachedDoc: "advertising" }));

      documents = [...documents, ...newDocs];

      // Prepare education array with saved records
      const education = savedEducationRecords.map((record) => ({
        courseName: record.courseName,
        courseDateOfPassing: convertDateForBackend(record.dateOfPassing),
        qualificationAcquir: convertQualificationTimingToCode(
          record.whenQualificationAcquired
        ),
        isCourseRecog: "Y", // Default to Y since course recognition is not stored
        courseDateOfAdmission: convertDateForBackend(record.dateOfAdmission),
        courceAfflitaed: record.courseAffiliated,
        qualification: convertQualificationTimingToCode(
          record.whenQualificationAcquired
        ), // Use qualification acquired as qualification
        documents: record.uploadedFiles
          .filter((f) => f.key && f.url) // Filter valid uploaded files
          .map((f) => ({
            fileName: f.key, // AWS upload key goes in fileName
            typeOfAttachedDoc: "education", // Document type
          })),
      }));

      // Prepare NOC request data for draft
      const nocRequestData: any = {
        ...(isEditMode && editId && { id: editId }), // Include ID for edit mode
        empId: user?.empId || user?.id || user?.employeeId || "", // Add empId for draft
        whetherProbation: formData.whetherProbation,
        is_draft: true,
        whetherProbationConfirmed: formData.whetherProbationConfirmed,
        threeyearsRegularServ: formData.whetherProbationConfirmed,
        dandARPendingOrNot: formData.dandARPendingOrNot,
        vigilancePending: formData.vigilancePending,

        trainedAbroadByDM: formData.trainedAbroadByDM,
        appNoInYear: formData.appNoInYear || "1", // Default to 1st application if not selected
        placeofPosting: formData.placeofPosting,
        natureOfOutside: formData.natureOfOutside,
        postAppliedFor: formData.postAppliedFor,
        newspaperWebsite: formData.newspaperWebsite,
        advertisementNo: formData.advertisementNo,
        organisationInWhich: formData.organizationApplied,
        placeAbroadTrain: formData.placeAbroadTrain,
        yearOfAbroadTrain: formData.yearOfAbroadTrain,
        education,
        documents,
      };

      // Normalize payload: backend expects single-character codes for Y/N-like fields.
      // Replace verbose 'Not Applicable' with a single-character code 'U' (for 'Unavailable')
      // to avoid DB facet maxlength=1 validation errors.
      Object.keys(nocRequestData).forEach((k) => {
        if (typeof nocRequestData[k] === "string" && nocRequestData[k] === "Not Applicable") {
          nocRequestData[k] = "U";
        }
      });

      // Only include date fields if they have values
      if (formData.advertisementDate && formData.advertisementDate.trim()) {
        nocRequestData.advertisementDate = formData.advertisementDate;
      }
      if (formData.lastDateReceipt && formData.lastDateReceipt.trim()) {
        nocRequestData.lastDateOfApplica = formData.lastDateReceipt;
      }

      // Only include dandARPenaltyEffective if it has a value
      const dandARPenaltyEffectiveDate = convertDateForBackend(
        formData.dandARPenaltyEffectiveDate
      );
      if (dandARPenaltyEffectiveDate) {
        nocRequestData.dandARPenaltyEffective = dandARPenaltyEffectiveDate;
      }
      // Remove vigilancePenaltyEffective from payload - don't send it

      const result = await dispatch(
        createNOCOutjobRequest(nocRequestData)
      ).unwrap();

      // Show success message based on API response
      if (result?.data?.message) {
        toast.success(result.data.message);
      } else if (result?.message) {
        toast.success(result.message);
      } else {
        toast.success(
          isEditMode
            ? "Draft updated successfully!"
            : "Draft saved successfully!"
        );
      }
      navigate(routes.noc.outsideJob);
    } catch (error: any) {
      console.error("Failed to save draft:", error);

      // Show specific error message based on error type and API response
      let errorMessage = "Failed to save draft. Please try again.";

      // Check various error response formats
      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      toast.error(errorMessage);
    } finally {
      setShowLoadingModal(false);
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Enable realtime validation when user tries to submit
    setEnableRealtimeValidation(true);

    // Validate form before proceeding
    const { isValid, firstErrorField, errors } = await validateForm();

    if (!isValid) {
      const firstErrorMsg =
        firstErrorField && errors ? errors[firstErrorField] : undefined;
      toast.error(
        firstErrorMsg || "Please fix the validation errors before submitting"
      );
      scrollToField(firstErrorField);
      return;
    }

    // Check if probation is confirmed before allowing submission
    if (formData.probationConfirmed === "N") {
      setShowProbationAlert(true);
      return;
    }

    setShowDeclarationModal(true);
  };

  const handleFinalSubmit = async () => {
    if (
      declarationChecks.informationTrue &&
      declarationChecks.eligibilityCriteria
    ) {
      setIsSubmitting(true);
      setShowLoadingModal(true);
      setLoadingMessage(
        isEditMode ? "Updating NOC request..." : "Submitting NOC request..."
      );

      try {
        // Validate required fields for final submission
        const requiredFields = [
          { field: formData.postAppliedFor, name: "Post Applied For" },
          { field: formData.organizationApplied, name: "Organization Applied" },
          {
            field: formData.natureOfOutside,
            name: "Nature of Outside Job",
          },
          { field: formData.advertisementNo, name: "Advertisement Number" },
          { field: formData.advertisementDate, name: "Advertisement Date" },
          { field: formData.lastDateReceipt, name: "Last Date of Receipt" },
          { field: formData.appNoInYear, name: "Application Number in Year" },
        ];

        const missingFields = requiredFields
          .filter((item) => !item.field?.trim())
          .map((item) => item.name);
        console.log("Form Data:", formData);
        console.log("Missing fields:", missingFields);

        if (missingFields.length > 0) {
          setIsSubmitting(false); // Reset submitting state
          setShowDeclarationModal(false); // Close the modal so user can see the form
          toast.error(
            `Please fill in the following required fields: ${missingFields.join(
              ", "
            )}`
          );
          return;
        }

        // Validate that last date of receipt is greater than current date
        if (formData.lastDateReceipt) {
          const lastDate = dayjs(formData.lastDateReceipt).startOf("day");
          const today = dayjs().startOf("day");

          if (lastDate.isBefore(today) || lastDate.isSame(today)) {
            setIsSubmitting(false);
            setShowDeclarationModal(false);
            toast.error(
              "Last date of receipt of application must be a future date (greater than today)"
            );
            return;
          }
        }

        if (savedEducationRecords.length === 0) {
          setIsSubmitting(false); // Reset submitting state
          setShowDeclarationModal(false); // Close the modal so user can see the form
          toast.error(
            "Please add at least one education record before submitting."
          );
          return;
        }

        // Check if advertisement documents are uploaded
        if (uploadedFiles.length === 0) {
          setIsSubmitting(false); // Reset submitting state
          setShowDeclarationModal(false); // Close the modal so user can see the form
          toast.error(
            "Please upload at least one advertisement document before submitting."
          );
          return;
        }

        // Check if all uploaded files have been processed successfully
        const failedUploads = uploadedFiles.filter(
          (f) => f.error || f.uploading
        );
        if (failedUploads.length > 0) {
          setIsSubmitting(false); // Reset submitting state
          setShowDeclarationModal(false); // Close the modal so user can see the form
          toast.error(
            "Please wait for all files to upload successfully or remove failed uploads before submitting."
          );
          return;
        }

        // Prepare documents array from uploaded files
        // In edit mode, preserve existing documents and add new ones
        let documents: Array<{
          fileName: string;
          typeOfAttachedDoc: string;
        }> = [];

        if (isEditMode && currentRequest?.documents) {
          // Keep existing documents (convert to the format expected by backend)
          const existingDocs = currentRequest.documents
            .filter((doc: any) => doc.typeOfAttachedDoc === "advertising")
            .map((doc: any) => ({
              fileName: doc.fileName,
              typeOfAttachedDoc: convertDocTypeToCode(doc.typeOfAttachedDoc),
              // Don't include ID - backend doesn't expect it
            }));
          documents = [...existingDocs];
        }

        // Add any newly uploaded files
        const newDocs = uploadedFiles
          .filter((f) => f.key && f.url) // Filter valid uploaded files
          .map((f) => ({ fileName: f.key, typeOfAttachedDoc: convertDocTypeToCode("advertising") }));

        documents = [...documents, ...newDocs];

        // Prepare education array with saved records
        const education = savedEducationRecords.map((record) => ({
          courseName: record.courseName,
          courseDateOfPassing: convertDateForBackend(record.dateOfPassing),
          qualificationAcquir: convertQualificationTimingToCode(
            record.whenQualificationAcquired
          ),
          isCourseRecog: "Y", // Default to Y since course recognition is not stored
          courseDateOfAdmission: convertDateForBackend(record.dateOfAdmission),
          courceAfflitaed: record.courseAffiliated,
          qualification: convertQualificationTimingToCode(
            record.whenQualificationAcquired
          ), // Use qualification acquired as qualification
          documents: record.uploadedFiles
            .filter((f) => f.key && f.url) // Filter valid uploaded files
            .map((f) => ({
              fileName: f.key, // AWS upload key goes in fileName
              typeOfAttachedDoc: convertDocTypeToCode("education"), // Document type
            })),
        }));

        // Prepare NOC request data for final submission
        const nocRequestData: any = {
          ...(isEditMode && editId && { id: editId }), // Include ID for edit mode
          empId: user?.empId || user?.id || user?.employeeId || "", // Add empId from user profile
          departmentName: formData.department,
          contactNumber: formData.contactNumber,
          emailId: formData.email,
          whetherProbation: formData.whetherProbation,
          is_draft: false,
          whetherProbationConfirmed: formData.whetherProbationConfirmed,
          threeyearsRegularServ: formData.whetherProbationConfirmed,
          dandARPendingOrNot: formData.dandARPendingOrNot,
          vigilancePending: formData.vigilancePending,
          trainedAbroadByDM: formData.trainedAbroadByDM,
          appNoInYear: formData.appNoInYear || "1", // Default to 1st application if not selected
          placeofPosting: formData.placeofPosting,
          natureOfOutside: formData.natureOfOutside,
          postAppliedFor: formData.postAppliedFor,
          newspaperWebsite: formData.newspaperWebsite,
          advertisementNo: formData.advertisementNo,
          organisationInWhich: formData.organizationApplied,
          placeAbroadTrain: formData.placeAbroadTrain,
          yearOfAbroadTrain: formData.yearOfAbroadTrain,
          education,
          documents,
        };

        // Normalize payload: replace any 'Not Applicable' with single-char code 'U'
        Object.keys(nocRequestData).forEach((k) => {
          if (typeof nocRequestData[k] === "string" && nocRequestData[k] === "Not Applicable") {
            nocRequestData[k] = "U";
          }
        });

        // Only include date fields if they have values
        if (formData.advertisementDate && formData.advertisementDate.trim()) {
          nocRequestData.advertisementDate = formData.advertisementDate;
        }
        if (formData.lastDateReceipt && formData.lastDateReceipt.trim()) {
          nocRequestData.lastDateOfApplica = formData.lastDateReceipt;
        }

        // Only include dandARPenaltyEffective if it has a value
        const dandARPenaltyEffectiveDate = convertDateForBackend(
          formData.dandARPenaltyEffectiveDate
        );
        if (dandARPenaltyEffectiveDate) {
          nocRequestData.dandARPenaltyEffective = dandARPenaltyEffectiveDate;
        }

        const result = await dispatch(
          createNOCOutjobRequest(nocRequestData)
        ).unwrap();

        setShowDeclarationModal(false);

        // Show success message in modal based on API response
        let successMessage = "";
        if (result?.data?.message) {
          successMessage = result.data.message;
        } else if (result?.message) {
          successMessage = result.message;
        } else {
          successMessage = isEditMode
            ? "NOC request updated successfully!"
            : "NOC request submitted successfully!";
        }

        setResponseMessage(successMessage);
        setResponseType("success");
        setShowResponseModal(true);
      } catch (error: any) {
        console.error("Failed to submit NOC request:", error);

        // Show specific error message in modal based on error type and API response
        let errorMessage = "Failed to submit NOC request. Please try again.";

        // Check various error response formats
        if (error?.data?.message) {
          errorMessage = error.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (typeof error === "string") {
          errorMessage = error;
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        }

        setResponseMessage(errorMessage);
        setResponseType("error");
        setShowResponseModal(true);
      } finally {
        setShowLoadingModal(false);
        setIsSubmitting(false);
      }
    } else {
      toast.error(
        "Please check both declaration checkboxes before submitting."
      );
    }
  };

  const handleEducationDetailsChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setEducationDetails((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      // Auto-fill whenQualificationAcquired based on dateOfPassing and formData.dateOfJoining
      if (name === "dateOfPassing" && value && formData.dateOfJoining) {
        try {
          const passDate = new Date(value); // value is YYYY-MM-DD
          const [day, month, year] = formData.dateOfJoining.split("-");
          const joinDate = new Date(Number(year), Number(month) - 1, Number(day));

          if (passDate <= joinDate) {
            updated.whenQualificationAcquired = "Before joining";
          } else {
            updated.whenQualificationAcquired = "After joining";
          }
        } catch (e) {
          console.error("Error auto-filling qualification timing:", e);
        }
      }

      return updated;
    });

    // Validate the field only if realtime validation is enabled
    if (enableRealtimeValidation) {
      validateEducationField(name, value);
    }
  };

  // Enhanced education file change handler with upload
  const handleEducationFileChangeWithUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, files } = e.target;
    if (files) {
      const fileArray = Array.from(files);

      // Add files to local state with uploading status
      const newFiles = fileArray.map((file) => ({
        file,
        key: "",
        url: "",
        uploading: true,
      }));

      setUploadedEducationFiles((prev) => [...prev, ...newFiles]);

      // Upload each file
      for (let i = 0; i < newFiles.length; i++) {
        const fileInfo = newFiles[i];
        const uploadResult = await uploadFile(fileInfo.file);

        if (uploadResult) {
          setUploadedEducationFiles((prev) =>
            prev.map((f) =>
              f.file === fileInfo.file
                ? {
                  ...f,
                  key: uploadResult.key,
                  url: uploadResult.url,
                  uploading: false,
                }
                : f
            )
          );
        } else {
          setUploadedEducationFiles((prev) =>
            prev.map((f) =>
              f.file === fileInfo.file
                ? { ...f, error: "Upload failed", uploading: false }
                : f
            )
          );
        }
      }
    }
  };

  // Remove education file and delete from AWS
  const removeEducationFileAndDelete = async (fileIndex: number) => {
    const fileToRemove = uploadedEducationFiles[fileIndex];

    if (fileToRemove.key) {
      try {
        await dispatch(deleteFileFromAWS({ key: fileToRemove.key })).unwrap();
      } catch (error) {
        console.error("Failed to delete file from AWS:", error);
      }
    }

    setUploadedEducationFiles((prev) =>
      prev.filter((_, index) => index !== fileIndex)
    );
  };

  // Enhanced education record save validation
  const saveEducationRecord = async () => {
    // Validate education details first
    const isValid = await validateEducationDetails();

    if (!isValid) {
      toast.error("Please fix the validation errors before saving");
      return;
    }

    // Validate required fields
    if (!educationDetails.courseName.trim()) {
      toast.error("Please enter course name");
      return;
    }

    // Validate date fields for logical sequence
    const admissionDate = educationDetails.dateOfAdmission
      ? dayjs(educationDetails.dateOfAdmission)
      : null;
    const passingDate = educationDetails.dateOfPassing
      ? dayjs(educationDetails.dateOfPassing)
      : null;

    // Check if passing date is after admission date
    if (admissionDate && passingDate && passingDate.isBefore(admissionDate)) {
      toast.error("Date of passing cannot be before date of admission");
      return;
    }

    // Check if all dates are not in future
    const today = dayjs();
    if (admissionDate && admissionDate.isAfter(today)) {
      toast.error("Date of admission cannot be in future");
      return;
    }
    if (passingDate && passingDate.isAfter(today)) {
      toast.error("Date of passing cannot be in future");
      return;
    }


    if (uploadedEducationFiles.length === 0) {
      toast.error("Please upload at least one document");
      return;
    }

    // Check if all files are uploaded successfully
    const pendingUploads = uploadedEducationFiles.filter(
      (f) => f.uploading || f.error
    );
    if (pendingUploads.length > 0) {
      toast.error(
        "Please wait for all files to upload successfully before saving"
      );
      return;
    }

    // Categorize uploaded files by type
    const categorizedFiles = uploadedEducationFiles.map((fileData) => ({
      file: fileData.file,
      key: fileData.key,
      url: fileData.url,
      type: fileData.file.name.toLowerCase().includes("noc")
        ? ("noc" as const)
        : fileData.file.name.toLowerCase().includes("education") ||
          fileData.file.name.toLowerCase().includes("qualification")
          ? ("education" as const)
          : ("advertising" as const),
    }));

    const newRecord = {
      id: Date.now().toString(),
      courseName: educationDetails.courseName,
      dateOfAdmission: educationDetails.dateOfAdmission,
      dateOfPassing: educationDetails.dateOfPassing,
      whenQualificationAcquired: educationDetails.whenQualificationAcquired,
      courseAffiliated: educationDetails.courseAffiliated,
      uploadedFiles: categorizedFiles,
      createdAt: new Date().toISOString(),
    };

    setSavedEducationRecords((prev) => [...prev, newRecord]);

    // Show success message
    toast.success("Education record saved successfully!");

    // Reset the form and close modal
    setEducationDetails({
      courseName: "",
      dateOfAdmission: "",
      dateOfPassing: "",
      whenQualificationAcquired: "",
      courseAffiliated: "",
      nocAdditionFiles: [],
      educationQualificationFiles: [],
      advertisingDocumentsFiles: [],
    });
    setUploadedEducationFiles([]);
    setEducationValidationErrors({});
    setShowEducationModal(false);
  };

  // Clear all education files and delete from AWS (for modal)
  const clearAllEducationFilesAndDelete = async () => {
    for (const fileData of uploadedEducationFiles) {
      if (fileData.key) {
        try {
          await dispatch(deleteFileFromAWS({ key: fileData.key })).unwrap();
        } catch (error) {
          console.error("Failed to delete file from AWS:", error);
        }
      }
    }

    setUploadedEducationFiles([]);
    setEducationDetails((prev) => ({
      ...prev,
      nocAdditionFiles: [],
      educationQualificationFiles: [],
      advertisingDocumentsFiles: [],
    }));

    // Clear all file inputs
    if (nocAdditionFileRef.current) nocAdditionFileRef.current.value = "";
    if (educationQualificationFileRef.current)
      educationQualificationFileRef.current.value = "";
    if (advertisingDocumentsFileRef.current)
      advertisingDocumentsFileRef.current.value = "";
  };

  const handleDeclarationChange = (field: string, checked: boolean) => {
    setDeclarationChecks((prev) => {
      const updated = {
        ...prev,
        [field]: checked,
      };
      return updated;
    });
  };

  // Helper function to convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to convert file to base64"));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Upload file to AWS
  const uploadFile = async (
    file: File
  ): Promise<{ key: string; url: string } | null> => {
    try {
      const base64String = await fileToBase64(file);
      const result = await dispatch(
        uploadFileToAWS({ filestring: base64String })
      ).unwrap();
      return {
        key: result.data.key,
        url: result.data.url,
      };
    } catch (error) {
      console.error("File upload failed:", error);
      return null;
    }
  };

  // Enhanced file change handler with upload
  const handleFileChangeWithUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, files } = e.target;
    if (files) {
      if (name === "uploadAdvertising") {
        const fileArray = Array.from(files);
        const validFiles: { file: File; key: string; url: string; uploading: boolean }[] = [];
        const errors: string[] = [];

        // Validate each file before processing
        fileArray.forEach((file) => {
          // Validate file type
          if (!validateFileType(file, ALLOWED_FILE_TYPES)) {
            errors.push(
              `${file.name}: File type not allowed. Allowed types: ${ALLOWED_FILE_TYPES.join(", ").toUpperCase()}`
            );
            return;
          }

          // Validate file size (2MB limit)
          if (!validateFileSize(file, MAX_FILE_SIZE_MB)) {
            errors.push(
              `${file.name}: File size (${formatFileSize(file.size)}) exceeds the maximum allowed size of ${MAX_FILE_SIZE_MB}MB. Please upload a smaller file.`
            );
            return;
          }

          validFiles.push({
            file,
            key: "",
            url: "",
            uploading: true,
          });
        });

        // Show validation errors
        if (errors.length > 0) {
          errors.forEach((error) => toast.error(error));
        }

        // If no valid files, return early
        if (validFiles.length === 0) {
          // Clear the input
          e.target.value = "";
          return;
        }

        // Add valid files to local state with uploading status
        setUploadedFiles((prev) => [...prev, ...validFiles]);

        // Upload each valid file
        for (let i = 0; i < validFiles.length; i++) {
          const fileInfo = validFiles[i];
          try {
            const uploadResult = await uploadFile(fileInfo.file);

            if (uploadResult) {
              setUploadedFiles((prev) =>
                prev.map((f) =>
                  f.file === fileInfo.file
                    ? {
                      ...f,
                      key: uploadResult.key,
                      url: uploadResult.url,
                      uploading: false,
                    }
                    : f
                )
              );
              // Show success toast for individual file upload
              toast.success(`"${fileInfo.file.name}" uploaded successfully`);
            } else {
              setUploadedFiles((prev) =>
                prev.map((f) =>
                  f.file === fileInfo.file
                    ? { ...f, error: "Upload failed", uploading: false }
                    : f
                )
              );
              // Show error toast for individual file upload failure
              toast.error(`Failed to upload "${fileInfo.file.name}"`);
            }
          } catch (error) {
            console.error("File upload error:", error);
            setUploadedFiles((prev) =>
              prev.map((f) =>
                f.file === fileInfo.file
                  ? { ...f, error: "Upload failed", uploading: false }
                  : f
              )
            );
            // Show error toast for individual file upload failure
            toast.error(`Failed to upload "${fileInfo.file.name}"`);
          }
        }
      }
    }
    // Clear the input to allow re-selecting the same file
    e.target.value = "";
  };

  // Remove file and delete from AWS
  const removeFileAndDelete = async (fileIndex: number) => {
    const fileToRemove = uploadedFiles[fileIndex];

    if (fileToRemove.key) {
      try {
        await dispatch(deleteFileFromAWS({ key: fileToRemove.key })).unwrap();
      } catch (error) {
        console.error("Failed to delete file from AWS:", error);
      }
    }

    setUploadedFiles((prev) => prev.filter((_, index) => index !== fileIndex));
  };

  const handleAttachmentClick = async (attachmentKey: string) => {
    try {
      // Dispatch the Redux action to get presigned URL
      const resultAction = await dispatch(getPresignedUrl(attachmentKey));

      if (getPresignedUrl.fulfilled.match(resultAction)) {
        // Open the file in a new tab using the signed URL
        window.open(resultAction.payload, "_blank");
      } else {
        console.error("Failed to get presigned URL:", resultAction.payload);
        alert("Unable to open file. Please try again.");
      }
    } catch (error) {
      console.error("Error opening attachment:", error);
      alert("Unable to open file. Please try again.");
    }
  };

  return (
    <>
      <style>
        {`
          .animate-spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Show message if in edit mode but no data found */}
      {isEditMode && !currentRequest && (
        <div className="page-wrapper">
          <div className="content">
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "400px" }}
            >
              <div className="text-center">
                <div className="alert alert-warning">
                  <i className="ti ti-alert-triangle me-2"></i>
                  NOC request not found in the current list.
                  <br />
                  Please go back to the list and try again, or the record may
                  have been deleted.
                </div>
                <div className="d-flex gap-2 justify-content-center">
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate(routes.noc.outsideJob)}
                  >
                    Back to List
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => window.location.reload()}
                  >
                    Refresh Page
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show form when not in edit mode or when data is available */}
      {(!isEditMode || currentRequest) && (
        <div className="page-wrapper">
          <div className="content">
            <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
              <div className="my-auto mb-2">
                <div className="d-flex align-items-center">
                  <Link
                    to={routes.noc.outsideJob}
                    className="text-primary text-decoration-none me-3"
                  >
                    <i className="ti ti-arrow-left fs-24"></i>
                  </Link>
                  <h3 className="page-title mb-0">
                    {isEditMode
                      ? "Edit NOC Application for Outside Job"
                      : "Application for NOC for Outside Job"}
                  </h3>
                </div>
                <nav className="mt-2">
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.employeeDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to={routes.noc.main}>NOC Services</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to={routes.noc.outsideJob}>Outside Job</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      {isEditMode ? "Edit Request" : "Create Request"}
                    </li>
                  </ol>
                </nav>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                {isLoadingProfile ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">
                        Loading profile...
                      </span>
                    </div>
                    <span className="ms-2">Loading your profile data...</span>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    {/* Required Documents - At the Top */}
                    <div className="mb-4 p-3 bg-info bg-opacity-10 border-start border-info border-4">
                      <h6 className="fw-bold text-info">
                        📄 Required Documents:
                      </h6>
                      <ul className="mb-0 ps-3 text-dark">
                        <li>
                          Complete advertisement with date and post details
                        </li>
                        <li>Job application form or relevant documents</li>
                        <li>
                          Any additional requirements as per advertisement
                        </li>
                        <li>Maximum file size: 2 MB per file</li>
                      </ul>
                    </div>

                    {/* Section 1: Employee Details */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h5 className="card-title mb-0">Employee Details</h5>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          {/* Name */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Name
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                style={{ backgroundColor: "#f8f9fa" }}
                                readOnly
                              />
                            </div>
                          </div>

                          {/* Designation */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Designation
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="designation"
                                value={formData.designation}
                                onChange={handleInputChange}
                                style={{ backgroundColor: "#f8f9fa" }}
                                readOnly
                              />
                            </div>
                          </div>

                          {/* Date of Joining in DMRC */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Date of Joining in DMRC
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="dateOfJoining"
                                value={formData.dateOfJoining}
                                onChange={handleInputChange}
                                style={{ backgroundColor: "#f8f9fa" }}
                                readOnly
                              />
                            </div>
                          </div>

                          {/* Employment Status */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Employment Status
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="employmentStatus"
                                value={formData.employmentStatus}
                                onChange={handleInputChange}
                                style={{ backgroundColor: "#f8f9fa" }}
                                readOnly
                              />
                            </div>
                          </div>

                          {/* Department */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Department{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="department"
                                value={formData.department}
                                onChange={handleInputChange}
                                required
                                placeholder="Enter department"
                              />
                            </div>
                          </div>

                          {/* Contact Number */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Contact Number{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="tel"
                                className="form-control"
                                name="contactNumber"
                                value={formData.contactNumber}
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                          </div>

                          {/* Email ID */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Email ID <span className="text-danger">*</span>
                              </label>
                              <input
                                type="email"
                                className="form-control"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                placeholder="Enter email address"
                              />
                            </div>
                          </div>

                          {/* Date of Birth */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Date of Birth
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="dateOfBirth"
                                value={formData.dateOfBirth}
                                onChange={handleInputChange}
                                style={{ backgroundColor: "#f8f9fa" }}
                                readOnly
                              />
                            </div>
                          </div>

                          {/* Age */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Age
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="age"
                                value={
                                  formData.age ||
                                  calculateAge(formData.dateOfBirth)
                                }
                                style={{ backgroundColor: "#f8f9fa" }}
                                readOnly
                              />
                            </div>
                          </div>

                          {/* Category */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Category
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                                style={{ backgroundColor: "#f8f9fa" }}
                                readOnly
                              />
                            </div>
                          </div>

                          {/* LWP */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                LWP
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="lwp"
                                value={formData.lwp}
                                onChange={handleInputChange}
                                style={{ backgroundColor: "#f8f9fa" }}
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Section 2: Probation Details */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h5 className="card-title mb-0">Probation Details</h5>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6">
                            <div
                              className="form-group mb-3"
                              ref={registerFieldRef("whetherProbation")}
                            >
                              <label className="form-label text-dark fw-medium">
                                Whether probation is confirmed in DMRC (Not
                                applicable in case of Absorption, after
                                deputation){" "}
                                <span className="text-danger">*</span>
                              </label>
                              <div>
                                <div className="form-check form-check-inline">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="whetherProbation"
                                    id="probation_yes"
                                    checked={formData.whetherProbation === "Y"}
                                    onChange={() =>
                                      handleRadioChange("whetherProbation", "Y")
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="probation_yes"
                                  >
                                    Yes
                                  </label>
                                </div>
                                <div className="form-check form-check-inline">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="whetherProbation"
                                    id="probation_no"
                                    checked={formData.whetherProbation === "N"}
                                    onChange={() =>
                                      handleRadioChange("whetherProbation", "N")
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="probation_no"
                                  >
                                    No
                                  </label>
                                </div>
                                <div className="form-check form-check-inline">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="whetherProbation"
                                    id="probation_na"
                                    checked={
                                      formData.whetherProbation ===
                                      "Not Applicable"
                                    }
                                    onChange={() =>
                                      handleRadioChange(
                                        "whetherProbation",
                                        "Not Applicable"
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="probation_na"
                                  >
                                    Not Applicable
                                  </label>
                                </div>
                                {validationErrors.whetherProbation && (
                                  <div className="text-danger small mt-2">
                                    {validationErrors.whetherProbation}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div
                              className="form-group mb-3"
                              ref={registerFieldRef(
                                "whetherProbationConfirmed"
                              )}
                            >
                              <label className="form-label text-dark fw-medium">
                                {formData.whetherProbation === "Not Applicable" 
                                  ? "Whether two years service completed after Absorption"
                                  : "Whether after probation confirmation, one year regular service complete"}
                                {" "}
                                <span className="text-danger">*</span>
                              </label>
                              <div>
                                <div className="form-check form-check-inline">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="whetherProbationConfirmed"
                                    id="regular_yes"
                                    checked={
                                      formData.whetherProbationConfirmed === "Y"
                                    }
                                    onChange={() =>
                                      handleRadioChange(
                                        "whetherProbationConfirmed",
                                        "Y"
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="regular_yes"
                                  >
                                    Yes
                                  </label>
                                </div>
                                <div className="form-check form-check-inline">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="whetherProbationConfirmed"
                                    id="regular_no"
                                    checked={
                                      formData.whetherProbationConfirmed === "N"
                                    }
                                    onChange={() =>
                                      handleRadioChange(
                                        "whetherProbationConfirmed",
                                        "N"
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="regular_no"
                                  >
                                    No
                                  </label>
                                </div>
                                {validationErrors.whetherProbationConfirmed && (
                                  <div className="text-danger small mt-2">
                                    {validationErrors.whetherProbationConfirmed}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div
                              className="form-group mb-3"
                              ref={registerFieldRef("trainedAbroadByDM")}
                            >
                              <label className="form-label text-dark fw-medium">
                                Has the employee been imported training abroad
                                by DMRC <span className="text-danger">*</span>
                              </label>
                              <div className="mt-2">
                                <div className="form-check form-check-inline">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="trainedAbroadByDM"
                                    id="training_yes"
                                    checked={formData.trainedAbroadByDM === "Y"}
                                    onChange={() =>
                                      handleRadioChange(
                                        "trainedAbroadByDM",
                                        "Y"
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="training_yes"
                                  >
                                    Yes
                                  </label>
                                </div>
                                <div className="form-check form-check-inline">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="trainedAbroadByDM"
                                    id="training_no"
                                    checked={formData.trainedAbroadByDM === "N"}
                                    onChange={() =>
                                      handleRadioChange(
                                        "trainedAbroadByDM",
                                        "N"
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="training_no"
                                  >
                                    No
                                  </label>
                                </div>
                                {validationErrors.trainedAbroadByDM && (
                                  <div className="text-danger small mt-2">
                                    {validationErrors.trainedAbroadByDM}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6"></div>
                          {/* Conditional fields for training abroad */}
                          {formData.trainedAbroadByDM === "Y" && (
                            <>
                              {/* Place of Training Abroad */}
                              <div className="col-md-6">
                                <div
                                  className="form-group mb-3"
                                  ref={registerFieldRef("placeAbroadTrain")}
                                >
                                  <label className="form-label text-dark fw-medium">
                                    Place of Training Abroad{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    className={`form-control ${validationErrors.placeAbroadTrain
                                      ? "is-invalid"
                                      : ""
                                      }`}
                                    name="placeAbroadTrain"
                                    value={formData.placeAbroadTrain}
                                    onChange={handleInputChange}
                                    required
                                    maxLength={50}
                                    placeholder="Enter place of training abroad"
                                  />
                                  {validationErrors.placeAbroadTrain && (
                                    <div className="text-danger small mt-1">
                                      {validationErrors.placeAbroadTrain}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Year of Training */}
                              <div className="col-md-6">
                                <div
                                  className="form-group mb-3"
                                  ref={registerFieldRef("yearOfAbroadTrain")}
                                >
                                  <label className="form-label text-dark fw-medium">
                                    Year of Training{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <ConfigProvider
                                    theme={{
                                      token: {
                                        colorPrimary: "#1890ff",
                                      },
                                    }}
                                  >
                                    <DatePicker
                                      className={`form-control ${validationErrors.yearOfAbroadTrain
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      picker="year"
                                      value={
                                        formData.yearOfAbroadTrain
                                          ? dayjs(
                                            formData.yearOfAbroadTrain,
                                            "YYYY"
                                          )
                                          : null
                                      }
                                      onChange={(date, dateString) => {
                                        setFormData((prev) => ({
                                          ...prev,
                                          yearOfAbroadTrain:
                                            dateString as string,
                                        }));
                                        if (enableRealtimeValidation) {
                                          validateField(
                                            "yearOfAbroadTrain",
                                            (dateString as string) || ""
                                          );
                                        }
                                      }}
                                      placeholder="Select year"
                                      style={{ width: "100%" }}
                                      format="YYYY"
                                    />
                                  </ConfigProvider>
                                  {validationErrors.yearOfAbroadTrain && (
                                    <div className="invalid-feedback d-block">
                                      {validationErrors.yearOfAbroadTrain}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Section 3: Advertisement Details */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h5 className="card-title mb-0">
                          Advertisement Details
                        </h5>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          {/* No of application forwarded in the year */}
                          <div className="col-md-6">
                            <div
                              className="form-group mb-3"
                              ref={registerFieldRef("appNoInYear")}
                            >
                              <label className="form-label text-dark fw-medium">
                                No. of application forwarded in the year{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <select
                                className={`form-select ${validationErrors.appNoInYear
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                name="appNoInYear"
                                value={formData.appNoInYear}
                                onChange={handleInputChange}
                                required
                              >
                                <option value="">
                                  Select application number
                                </option>
                                <option value="1">1st</option>
                                <option value="2">2nd</option>
                                <option value="3">3rd</option>
                                <option value="4">4th</option>
                              </select>
                              {validationErrors.appNoInYear && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.appNoInYear}
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Place of Posting */}
                          <div className="col-md-6">
                            <div
                              className="form-group mb-3"
                              ref={registerFieldRef("placeofPosting")}
                            >
                              <label className="form-label text-dark fw-medium">
                                Place of Posting{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${validationErrors.placeofPosting
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                name="placeofPosting"
                                value={formData.placeofPosting}
                                onChange={handleInputChange}
                                required
                                maxLength={50}
                              />
                              {validationErrors.placeofPosting && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.placeofPosting}
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Nature of Outside Job */}
                          <div className="col-md-6">
                            <div
                              className="form-group mb-3"
                              ref={registerFieldRef("natureOfOutside")}
                            >
                              <label className="form-label text-dark fw-medium">
                                Nature of Outside Job{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <select
                                className={`form-select ${validationErrors.natureOfOutside
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                name="natureOfOutside"
                                value={formData.natureOfOutside}
                                onChange={handleInputChange}
                                required
                                disabled={isLoadingJobNature}
                              >
                                <option value="">
                                  {isLoadingJobNature
                                    ? "Loading options..."
                                    : "Select Nature"}
                                </option>
                                {outsideJobNatureOptions.map((option) => (
                                  <option
                                    key={option.sap_master_natures_id}
                                    value={option.sap_master_natures_natureId}
                                  >
                                    {option.sap_master_natures_natureTitle}
                                  </option>
                                ))}
                              </select>
                              {validationErrors.natureOfOutside && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.natureOfOutside}
                                </div>
                              )}
                              {isLoadingJobNature && (
                                <div className="form-text text-muted">
                                  <i className="ti ti-loader-2 animate-spin me-1"></i>
                                  Loading job nature options...
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Name of Post applied for */}
                          <div className="col-md-6">
                            <div
                              className="form-group mb-3"
                              ref={registerFieldRef("postAppliedFor")}
                            >
                              <label className="form-label text-dark fw-medium">
                                Name of Post applied for{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${validationErrors.postAppliedFor
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                name="postAppliedFor"
                                value={formData.postAppliedFor}
                                onChange={handleInputChange}
                                required
                                maxLength={50}
                              />
                              {validationErrors.postAppliedFor && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.postAppliedFor}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Newspaper/website */}
                          <div className="col-md-6">
                            <div
                              className="form-group mb-3"
                              ref={registerFieldRef("newspaperWebsite")}
                            >
                              <label className="form-label text-dark fw-medium">
                                Newspaper/website{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${validationErrors.newspaperWebsite
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                name="newspaperWebsite"
                                value={formData.newspaperWebsite}
                                onChange={handleInputChange}
                                required
                                maxLength={50}
                              />
                              {validationErrors.newspaperWebsite && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.newspaperWebsite}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Advertisement No */}
                          <div className="col-md-6">
                            <div
                              className="form-group mb-3"
                              ref={registerFieldRef("advertisementNo")}
                            >
                              <label className="form-label text-dark fw-medium">
                                Advertisement No{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${validationErrors.advertisementNo
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                name="advertisementNo"
                                value={formData.advertisementNo}
                                onChange={handleInputChange}
                                required
                                maxLength={50}
                              />
                              {validationErrors.advertisementNo && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.advertisementNo}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Advertisement Date */}
                          <div className="col-md-6">
                            <div
                              className="form-group mb-3"
                              ref={registerFieldRef("advertisementDate")}
                            >
                              <label className="form-label text-dark fw-medium">
                                Advertisement Date{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <ConfigProvider
                                theme={{
                                  token: {
                                    colorPrimary: "#dc3545",
                                    borderRadius: 6,
                                  },
                                }}
                              >
                                <DatePicker
                                  className="form-control"
                                  value={
                                    formData.advertisementDate
                                      ? dayjs(formData.advertisementDate)
                                      : null
                                  }
                                  onChange={(date) => {
                                    handleInputChange({
                                      target: {
                                        name: "advertisementDate",
                                        value: date
                                          ? date.format("YYYY-MM-DD")
                                          : "",
                                      },
                                    } as any);
                                  }}
                                  format="DD/MM/YYYY"
                                  placeholder="Select advertisement date"
                                  style={{
                                    width: "100%",
                                    height: "42px",
                                    fontSize: "14px",
                                  }}
                                  allowClear={true}
                                  showToday={true}
                                  disabledDate={(current) => {
                                    // Disable future dates beyond today
                                    return (
                                      current && current > dayjs().endOf("day")
                                    );
                                  }}
                                />
                              </ConfigProvider>
                              {validationErrors.advertisementDate && (
                                <div className="text-danger small mt-2">
                                  {validationErrors.advertisementDate}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Name of the organization */}
                          <div className="col-md-6">
                            <div
                              className="form-group mb-3"
                              ref={registerFieldRef("organizationApplied")}
                            >
                              <label className="form-label text-dark fw-medium">
                                Name of the organization in which applied{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${validationErrors.organizationApplied
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                name="organizationApplied"
                                value={formData.organizationApplied}
                                onChange={handleInputChange}
                                required
                                maxLength={50}
                              />
                              {validationErrors.organizationApplied && (
                                <div className="text-danger small mt-1">
                                  {validationErrors.organizationApplied}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Last date of receipt */}
                          <div className="col-md-6">
                            <div
                              className="form-group mb-3"
                              ref={registerFieldRef("lastDateReceipt")}
                            >
                              <label className="form-label text-dark fw-medium">
                                Last date of receipt of application{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <ConfigProvider
                                theme={{
                                  token: {
                                    colorPrimary: "#dc3545",
                                    borderRadius: 6,
                                  },
                                }}
                              >
                                <DatePicker
                                  className="form-control"
                                  value={
                                    formData.lastDateReceipt
                                      ? dayjs(formData.lastDateReceipt)
                                      : null
                                  }
                                  onChange={(date) => {
                                    handleInputChange({
                                      target: {
                                        name: "lastDateReceipt",
                                        value: date
                                          ? date.format("YYYY-MM-DD")
                                          : "",
                                      },
                                    } as any);
                                  }}
                                  format="DD/MM/YYYY"
                                  placeholder="Select last date of receipt"
                                  style={{
                                    width: "100%",
                                    height: "42px",
                                    fontSize: "14px",
                                  }}
                                  allowClear={true}
                                  showToday={true}
                                  disabledDate={(current) => {
                                    if (!current) return false;

                                    const today = dayjs().startOf("day");
                                    const advDate = formData.advertisementDate
                                      ? dayjs(formData.advertisementDate)
                                      : null;

                                    // Disable if date is before today (must be in future)
                                    if (current < today) {
                                      return true;
                                    }

                                    // Disable if date is before advertisement date
                                    if (
                                      advDate &&
                                      current < advDate.startOf("day")
                                    ) {
                                      return true;
                                    }

                                    return false;
                                  }}
                                />
                              </ConfigProvider>
                              {validationErrors.lastDateReceipt && (
                                <div className="text-danger small mt-2">
                                  {validationErrors.lastDateReceipt}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Upload Advertising documents */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Upload Advertisement Documents{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <div className="mb-3">
                                <input
                                  type="file"
                                  className="form-control"
                                  name="uploadAdvertising"
                                  ref={advertisementFileInputRef}
                                  onChange={handleFileChangeWithUpload}
                                  multiple
                                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                  required={uploadedFiles.length === 0}
                                />
                                <div className="form-text">
                                  Select multiple files to upload. Supported
                                  formats: PDF, JPG, PNG, DOC, DOCX. Maximum file size: 2MB per file.
                                  {formData.uploadAdvertising.length > 0 && (
                                    <span className="text-success ms-2">
                                      <i className="ti ti-check-circle me-1"></i>
                                      {formData.uploadAdvertising.length}{" "}
                                      file(s) selected
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* List of uploaded advertisement files */}
                          {uploadedFiles.length > 0 && (
                            <div className="col-12 mt-3">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold text-secondary mb-0">
                                  Uploaded Files ({uploadedFiles.length}):
                                </h6>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => setUploadedFiles([])}
                                  title="Clear all files"
                                >
                                  <i className="ti ti-trash me-1"></i>
                                  Clear All
                                </button>
                              </div>
                              <div className="list-group">
                                {uploadedFiles.map((fileData, index) => (
                                  <div
                                    key={index}
                                    className="list-group-item d-flex justify-content-between align-items-center"
                                  >
                                    <div className="d-flex align-items-center flex-grow-1">
                                      <i className="ti ti-file-text me-2 text-primary fs-4"></i>
                                      <div className="flex-grow-1">
                                        <div className="fw-medium">
                                          {fileData.file.name ||
                                            `File ${index + 1}`}
                                        </div>
                                        <small className="text-muted">
                                          {fileData.file.size ? (
                                            <>
                                              {(
                                                fileData.file.size /
                                                1024 /
                                                1024
                                              ).toFixed(2)}{" "}
                                              MB
                                            </>
                                          ) : (
                                            "Uploaded successfully"
                                          )}
                                        </small>
                                        {fileData.uploading && (
                                          <div className="text-info small mt-1">
                                            <i className="ti ti-loader-2 animate-spin me-1"></i>
                                            Uploading...
                                          </div>
                                        )}
                                        {fileData.error && (
                                          <div className="text-danger small mt-1">
                                            <i className="ti ti-alert-circle me-1"></i>
                                            {fileData.error}
                                          </div>
                                        )}
                                        {fileData.key &&
                                          !fileData.error &&
                                          !fileData.uploading && (
                                            <div className="text-success small mt-1">
                                              <i className="ti ti-check-circle me-1"></i>
                                              Uploaded successfully
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                    <div className="d-flex gap-2">
                                      {fileData.key &&
                                        !fileData.uploading &&
                                        !fileData.error && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              const key = fileData?.file?.name;
                                              handleAttachmentClick(key);
                                            }}
                                            title="View file"
                                          >
                                            <i className="ti ti-eye"></i>
                                          </button>
                                        )}
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() =>
                                          removeFileAndDelete(index)
                                        }
                                        disabled={fileData.uploading}
                                        title="Remove file"
                                      >
                                        <i className="ti ti-x"></i>
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Section 4: Education Details */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h5 className="card-title mb-0">Education Details <span className="text-danger">*</span> </h5>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          {/* Original Education Details Upload */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Upload Education details as per the
                                advertisement Criteria{" "}
                                <span
                                  className="btn btn-sm btn-outline-primary ms-2"
                                  onClick={() => setShowEducationModal(true)}
                                >
                                  Click here
                                </span>
                              </label>
                            </div>
                          </div>

                          {/* Saved Education Records Table */}
                          {savedEducationRecords.length > 0 && (
                            <div className="col-md-12 mt-4">
                              <div className="card border">
                                <div className="card-header bg-light">
                                  <h6 className="mb-0 fw-bold text-primary">
                                    <i className="ti ti-school me-2"></i>
                                    Saved Education Records (
                                    {savedEducationRecords.length})
                                  </h6>
                                </div>
                                <div className="card-body p-0">
                                  <div className="table-responsive">
                                    <table className="table table-striped mb-0">
                                      <thead className="table-light">
                                        <tr>
                                          <th>Course Name</th>
                                          <th>Date of Admission</th>
                                          <th>Date of Passing</th>
                                          <th>Qualification Acquired</th>
                                          <th>Course Affiliated</th>
                                          <th>Files</th>
                                          <th>Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {savedEducationRecords.map((record) => (
                                          <tr key={record.id}>
                                            <td>
                                              <span className="fw-medium">
                                                {record.courseName}
                                              </span>
                                            </td>
                                            <td>
                                              {record.dateOfAdmission
                                                ? dayjs(
                                                  record.dateOfAdmission
                                                ).format("DD/MM/YYYY")
                                                : "-"}
                                            </td>
                                            <td>
                                              {record.dateOfPassing
                                                ? dayjs(
                                                  record.dateOfPassing
                                                ).format("DD/MM/YYYY")
                                                : "-"}
                                            </td>
                                            <td>
                                              <span
                                                className={`badge ${(record.whenQualificationAcquired === "B" || record.whenQualificationAcquired === "Before joining" || record.whenQualificationAcquired === "Before Joining")
                                                  ? "bg-success"
                                                  : (record.whenQualificationAcquired === "A" || record.whenQualificationAcquired === "After joining" || record.whenQualificationAcquired === "After Joining")
                                                    ? "bg-warning text-dark"
                                                    : "bg-secondary"
                                                  }`}
                                              >
                                                {(record.whenQualificationAcquired === "B" || record.whenQualificationAcquired === "Before joining" || record.whenQualificationAcquired === "Before Joining")
                                                  ? "Before Joining"
                                                  : (record.whenQualificationAcquired === "A" || record.whenQualificationAcquired === "After joining" || record.whenQualificationAcquired === "After Joining")
                                                    ? "After Joining"
                                                    : "Not specified"}
                                              </span>
                                            </td>
                                            <td>
                                              <span
                                                className={`badge ${record.courseAffiliated ===
                                                  "Yes"
                                                  ? "bg-success"
                                                  : record.courseAffiliated ===
                                                    "No"
                                                    ? "bg-danger"
                                                    : "bg-secondary"
                                                  }`}
                                              >
                                                {record.courseAffiliated ||
                                                  "Not specified"}
                                              </span>
                                            </td>
                                            <td>
                                              <div className="d-flex flex-wrap gap-1">
                                                {record.uploadedFiles.map(
                                                  (file, fileIndex) => (
                                                    <Link
                                                      to={"#"}
                                                      onClick={() =>
                                                        handleAttachmentClick(
                                                          file?.file?.name
                                                        )
                                                      }
                                                    >
                                                      <span
                                                        key={fileIndex}
                                                        className={`badge ${file.type === "noc"
                                                          ? "bg-info"
                                                          : file.type ===
                                                            "education"
                                                            ? "bg-success"
                                                            : "bg-warning"
                                                          }`}
                                                        title={`File ${fileIndex + 1
                                                          }`}
                                                      >
                                                        <i className="ti ti-file-text me-1"></i>
                                                        File {fileIndex + 1}
                                                      </span>
                                                    </Link>
                                                  )
                                                )}
                                              </div>
                                              <small className="text-muted">
                                                {record.uploadedFiles.length}{" "}
                                                file(s)
                                              </small>
                                            </td>
                                            <td>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() =>
                                                  removeEducationRecord(
                                                    record.id
                                                  )
                                                }
                                                title="Remove record"
                                              >
                                                <i className="ti ti-trash"></i>
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Whether any penalty imposed during last 10 years */}
                    <div className="col-md-12">
                      <div
                        className="form-group mb-3"
                        ref={registerFieldRef("dandARPendingOrNot")}
                      >
                        <label className="form-label">
                          Whether any penalty imposed, during last 10 years in
                          any D&AR case
                        </label>
                        <div className="mt-2">
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="dandARPendingOrNot"
                              id="penalty10_yes"
                              checked={formData.dandARPendingOrNot === "Y"}
                              onChange={() =>
                                handleRadioChange("dandARPendingOrNot", "Y")
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="penalty10_yes"
                            >
                              Yes
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="dandARPendingOrNot"
                              id="penalty10_no"
                              checked={formData.dandARPendingOrNot === "N"}
                              onChange={() =>
                                handleRadioChange("dandARPendingOrNot", "N")
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="penalty10_no"
                            >
                              No
                            </label>
                          </div>
                        </div>
                        {validationErrors.dandARPendingOrNot && (
                          <div className="text-danger small mt-2">
                            {validationErrors.dandARPendingOrNot}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Whether any penalty imposed during last 10 years in any Vigilance */}
                    <div className="col-md-12">
                      <div
                        className="form-group mb-3"
                        ref={registerFieldRef("vigilancePending")}
                      >
                        <label className="form-label">
                          Whether any penalty imposed, during last 10 years in
                          any Vigilance case
                        </label>
                        <div className="mt-2">
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="vigilancePending"
                              id="penalty_vig_yes"
                              checked={formData.vigilancePending === "Y"}
                              onChange={() =>
                                handleRadioChange("vigilancePending", "Y")
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="penalty_vig_yes"
                            >
                              Yes
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="vigilancePending"
                              id="penalty_vig_no"
                              checked={formData.vigilancePending === "N"}
                              onChange={() =>
                                handleRadioChange("vigilancePending", "N")
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="penalty_vig_no"
                            >
                              No
                            </label>
                          </div>
                        </div>
                        {validationErrors.vigilancePending && (
                          <div className="text-danger small mt-2">
                            {validationErrors.vigilancePending}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-center gap-3 mt-4">
                      <Link
                        to={routes.noc.outsideJob}
                        className="btn btn-outline-secondary px-4"
                      >
                        <i className="ti ti-arrow-left me-1"></i>
                        Cancel
                      </Link>
                      <button
                        type="button"
                        className="btn btn-secondary px-4"
                        onClick={handleSaveDraft}
                        disabled={isSubmitting}
                      >
                        <i className="ti ti-device-floppy me-1"></i>
                        Save as Draft
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary px-4"
                        disabled={isSubmitting}
                      >
                        <i className="ti ti-send me-1"></i>
                        Submit
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>

            {/* Overview Modal */}
            {showOverviewModal && (
              <>
                {/* Backdrop */}
                <div
                  className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
                  style={{ zIndex: 1040 }}
                />

                {/* Overview Modal */}
                <div
                  className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
                  style={{
                    zIndex: 1050,
                    width: "90%",
                    maxWidth: "600px",
                    maxHeight: "90vh",
                    overflowY: "auto",
                  }}
                >
                  {/* Modal Header */}
                  <div className="d-flex align-items-center justify-content-between p-3 border-bottom bg-light">
                    <h5 className="mb-0 text-dark fw-bold">
                      NOC for outside job overview
                    </h5>
                    <button
                      className="btn btn-link text-danger text-decoration-none p-0"
                      onClick={() => setShowOverviewModal(false)}
                    >
                      <i className="ti ti-x fs-24"></i>
                    </button>
                  </div>

                  {/* Modal Content */}
                  <div className="p-4" style={{ lineHeight: "1.6" }}>
                    <div className="text-dark">
                      <p className="mb-3">
                        <strong>1.</strong> {tBoth("noc_overview_point_1")}
                      </p>

                      <p className="mb-3">
                        <strong>2.</strong> {tBoth("noc_overview_point_2")}
                      </p>

                      <p className="mb-3">
                        <strong>3.</strong> {tBoth("noc_overview_point_3")}
                      </p>

                      <p className="mb-3">
                        <strong>4.</strong> {tBoth("noc_overview_point_4")}
                      </p>

                      <p className="mb-3">
                        <strong>5.</strong> {tBoth("noc_overview_point_5")}
                      </p>

                      <p className="mb-3">
                        <strong>6.</strong> {tBoth("noc_overview_point_6")}
                      </p>

                      <p className="mb-3">
                        <strong>7.</strong> {tBoth("noc_overview_point_7")}
                      </p>

                      <p className="mb-3">
                        <strong>8.</strong> {tBoth("noc_overview_point_8")}
                      </p>
                    </div>
                  </div>

                  {/* OK Button */}
                  <div className="d-flex justify-content-end p-4 pt-0">
                    <button
                      type="button"
                      className="btn text-white px-5 py-2 fw-bold"
                      style={{
                        backgroundColor: "#B85450",
                        borderRadius: "25px",
                        fontSize: "16px",
                      }}
                      onClick={() => setShowOverviewModal(false)}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Education Details Modal */}
            {showEducationModal && (
              <>
                {/* Backdrop */}
                <div
                  className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
                  style={{ zIndex: 1040 }}
                  onClick={() => setShowEducationModal(false)}
                />

                {/* Education Modal */}
                <div
                  className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
                  style={{
                    zIndex: 1050,
                    width: "90%",
                    maxWidth: "700px",
                    maxHeight: "90vh",
                    overflowY: "auto",
                  }}
                >
                  {/* Modal Header */}
                  <div className="d-flex align-items-center justify-content-between p-3 border-bottom text-dark">
                    <h5 className="mb-0">
                      Application for NOC for Outside Job
                    </h5>
                    <button
                      className="btn btn-link text-dark text-decoration-none p-0"
                      onClick={() => setShowEducationModal(false)}
                    >
                      <i className="ti ti-x fs-20"></i>
                    </button>
                  </div>

                  {/* Mandatory Attachments */}
                  <div className="p-3 bg-info bg-opacity-10 border-start border-info border-4">
                    <h6 className="fw-bold text-info">
                      Mandatory Attachments:
                    </h6>
                    <ul className="mb-0 ps-3 text-dark">
                      <li className="text-dark">
                        Copy of advertisement indicating last date of
                        application and post applied for must be attached.
                      </li>
                      <li className="text-dark">
                        If, with the existing educational qualifications (as
                        captured in SAP), the employee is not eligible for the
                        post, which require additional educational qualification
                        documents is mandatory, on basis of which the employee
                        is eligible for the post that he/she intends to apply
                        for.
                      </li>
                      <li className="text-dark">
                        Documents to be uploaded - Final Marksheet/Certificate
                      </li>
                    </ul>
                  </div>

                  {/* Upload Education Details Section */}
                  <div className="p-3 bg-light">
                    <h6 className="fw-bold text-primary">
                      Upload Education details:
                    </h6>
                  </div>

                  {/* Form Fields */}
                  <div className="p-3">
                    <div className="row">
                      <div className="col-12 mb-3">
                        <label className="form-label text-dark fw-medium">
                          Course Name: <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${educationValidationErrors.courseName
                            ? "is-invalid"
                            : ""
                            }`}
                          name="courseName"
                          value={educationDetails.courseName}
                          onChange={handleEducationDetailsChange}
                          placeholder="Enter course name"
                          maxLength={50}
                        />
                        {educationValidationErrors.courseName && (
                          <div className="text-danger small mt-1">
                            {educationValidationErrors.courseName}
                          </div>
                        )}
                      </div>

                      <div className="col-4 mb-3">
                        <label className="form-label text-dark fw-medium">
                          Date of admission: <span className="text-danger">*</span>
                        </label>
                        <div className="position-relative">
                          <ConfigProvider
                            theme={{
                              token: {
                                colorPrimary: "#dc3545",
                                borderRadius: 6,
                              },
                            }}
                          >
                            <DatePicker
                              className="form-control"
                              value={
                                educationDetails.dateOfAdmission
                                  ? dayjs(educationDetails.dateOfAdmission)
                                  : null
                              }
                              onChange={(date) => {
                                handleEducationDetailsChange({
                                  target: {
                                    name: "dateOfAdmission",
                                    value: date
                                      ? date.format("YYYY-MM-DD")
                                      : "",
                                  },
                                } as any);
                              }}
                              format="DD/MM/YYYY"
                              placeholder="Select admission date"
                              style={{
                                width: "100%",
                                height: "38px",
                                fontSize: "14px",
                              }}
                              allowClear={true}
                              showToday={false}
                              disabledDate={(current) => {
                                // Disable future dates beyond today
                                return (
                                  current && current > dayjs().endOf("day")
                                );
                              }}
                            />
                          </ConfigProvider>
                        </div>
                        {educationValidationErrors.dateOfAdmission && (
                          <div className="text-danger small mt-1">
                            {educationValidationErrors.dateOfAdmission}
                          </div>
                        )}
                      </div>

                      <div className="col-4 mb-3">
                        <label className="form-label text-dark fw-medium">
                          Date of Passing: <span className="text-danger">*</span>
                        </label>
                        <div className="position-relative">
                          <ConfigProvider
                            theme={{
                              token: {
                                colorPrimary: "#dc3545",
                                borderRadius: 6,
                              },
                            }}
                          >
                            <DatePicker
                              className="form-control"
                              value={
                                educationDetails.dateOfPassing
                                  ? dayjs(educationDetails.dateOfPassing)
                                  : null
                              }
                              onChange={(date) => {
                                handleEducationDetailsChange({
                                  target: {
                                    name: "dateOfPassing",
                                    value: date
                                      ? date.format("YYYY-MM-DD")
                                      : "",
                                  },
                                } as any);
                              }}
                              format="DD/MM/YYYY"
                              placeholder="Select passing date"
                              style={{
                                width: "100%",
                                height: "38px",
                                fontSize: "14px",
                              }}
                              allowClear={true}
                              showToday={false}
                              disabledDate={(current) => {
                                // Disable future dates and dates before admission
                                const admissionDate =
                                  educationDetails.dateOfAdmission
                                    ? dayjs(educationDetails.dateOfAdmission)
                                    : null;

                                if (current && current > dayjs().endOf("day")) {
                                  return true; // Disable future dates
                                }

                                if (admissionDate && current) {
                                  return current < admissionDate.startOf("day"); // Disable dates before admission
                                }

                                return false;
                              }}
                            />
                          </ConfigProvider>
                        </div>
                        {educationValidationErrors.dateOfPassing && (
                          <div className="text-danger small mt-1">
                            {educationValidationErrors.dateOfPassing}
                          </div>
                        )}
                      </div>

                      <div className="col-4 mb-3">
                        <label className="form-label text-dark fw-medium">
                          When Qualification acquired: <span className="text-danger">*</span>
                        </label>
                        <select
                          className={`form-select ${educationValidationErrors.whenQualificationAcquired
                            ? "is-invalid"
                            : ""
                            }`}
                          name="whenQualificationAcquired"
                          value={educationDetails.whenQualificationAcquired}
                          onChange={handleEducationDetailsChange}
                          disabled={false}
                          style={{
                            height: "38px",
                            fontSize: "14px",
                            borderRadius: "6px",
                            border: "1px solid #ced4da",
                          }}
                        >
                          <option value=""></option>
                          <option value="Before joining">Before joining</option>
                          <option value="After joining">After joining</option>
                        </select>
                        {educationValidationErrors.whenQualificationAcquired && (
                          <div className="text-danger small mt-1">
                            {
                              educationValidationErrors.whenQualificationAcquired
                            }
                          </div>
                        )}
                      </div>

                      <div className="col-12 mb-3">
                        <label className="form-label text-dark fw-medium">
                          Course affiliated by UGC/AICTE <span className="text-danger">*</span>
                        </label>
                        <div className="mt-2">
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="courseAffiliated"
                              id="courseAffiliated_yes"
                              value="Yes"
                              checked={
                                educationDetails.courseAffiliated === "Yes"
                              }
                              onChange={handleEducationDetailsChange}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="courseAffiliated_yes"
                            >
                              Yes
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="courseAffiliated"
                              id="courseAffiliated_no"
                              value="No"
                              checked={
                                educationDetails.courseAffiliated === "No"
                              }
                              onChange={handleEducationDetailsChange}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="courseAffiliated_no"
                            >
                              No
                            </label>
                          </div>
                        </div>
                        {educationValidationErrors.courseAffiliated && (
                          <div className="text-danger small mt-1">
                            {educationValidationErrors.courseAffiliated}
                          </div>
                        )}
                      </div>

                      {/* {educationDetails.dateOfAdmission && (
                        <div className="col-12 mb-3">
                          <label className="form-label text-dark fw-medium">
                            Upload NOC/Addition Office Order* <br />
                            <small className="text-muted">
                              (in case of course completed after joining DMRC)
                            </small>
                          </label>
                          <div className="d-flex align-items-center gap-2">
                            <input
                              type="file"
                              className="form-control"
                              name="nocAdditionFiles"
                              ref={nocAdditionFileRef}
                              onChange={handleEducationFileChangeWithUpload}
                              accept=".pdf,.doc,.docx"
                              style={{ display: "none" }}
                              id="nocAdditionFiles"
                              multiple
                            />
                            <button
                              type="button"
                              className="btn btn-outline-danger d-flex align-items-center text-decoration-none"
                              onClick={() =>
                                nocAdditionFileRef.current?.click()
                              }
                            >
                              <i className="ti ti-file-upload me-1 text-danger"></i>
                              Choose Files
                            </button>
                            {uploadedEducationFiles.filter((f) =>
                              f.file.name.includes("noc")
                            ).length > 0 && (
                                <span className="text-success">
                                  <i className="ti ti-check-circle me-1"></i>
                                  {
                                    uploadedEducationFiles.filter((f) =>
                                      f.file.name.includes("noc")
                                    ).length
                                  }{" "}
                                  file(s) uploaded
                                </span>
                              )}
                          </div>
                        </div>
                      )} */}

                      <div className="col-12 mb-3">
                        <label className="form-label text-dark fw-medium">
                          Upload Educational Qualification degree certificates*
                        </label>
                        <div className="d-flex align-items-center gap-2">
                          <input
                            type="file"
                            className="form-control"
                            name="educationQualificationFiles"
                            ref={educationQualificationFileRef}
                            onChange={handleEducationFileChangeWithUpload}
                            accept=".pdf,.doc,.docx"
                            style={{ display: "none" }}
                            id="educationQualificationFiles"
                            multiple
                          />
                          <button
                            type="button"
                            className="btn btn-outline-danger d-flex align-items-center text-decoration-none"
                            onClick={() =>
                              educationQualificationFileRef.current?.click()
                            }
                          >
                            <i className="ti ti-file-upload me-1 text-danger"></i>
                            Choose Files
                          </button>
                          {uploadedEducationFiles.filter(
                            (f) =>
                              f.file.name.includes("education") ||
                              f.file.name.includes("qualification")
                          ).length > 0 && (
                              <span className="text-success">
                                <i className="ti ti-check-circle me-1"></i>
                                {
                                  uploadedEducationFiles.filter(
                                    (f) =>
                                      f.file.name.includes("education") ||
                                      f.file.name.includes("qualification")
                                  ).length
                                }{" "}
                                file(s) uploaded
                              </span>
                            )}
                        </div>
                      </div>
                    </div>

                    {/* AWS Uploaded Files List Section */}
                    <div className="p-3 border-top">
                      {uploadedEducationFiles.length > 0 && (
                        <>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="fw-bold mb-0">
                              Uploaded Files ({uploadedEducationFiles.length}):
                            </h6>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={clearAllEducationFilesAndDelete}
                              title="Clear all files"
                            >
                              <i className="ti ti-trash me-1"></i>
                              Clear All
                            </button>
                          </div>
                          <div className="bg-light rounded p-3">
                            {uploadedEducationFiles.map((fileData, index) => (
                              <div
                                key={index}
                                className="d-flex align-items-center justify-content-between mb-2 last:mb-0"
                              >
                                <div className="d-flex align-items-center">
                                  <i className="ti ti-file-text text-primary me-2 fs-20"></i>
                                  <div>
                                    <div className="fw-medium text-dark">
                                      {fileData.file.name}
                                    </div>
                                    <small className="text-muted">
                                      {fileData.uploading ? (
                                        <span className="text-info">
                                          <i className="ti ti-loader-2 animate-spin me-1"></i>
                                          Uploading...
                                        </span>
                                      ) : fileData.error ? (
                                        <span className="text-danger">
                                          <i className="ti ti-alert-circle me-1"></i>
                                          {fileData.error}
                                        </span>
                                      ) : fileData.key ? (
                                        <span className="text-success">
                                          <i className="ti ti-check-circle me-1"></i>
                                          Uploaded successfully
                                        </span>
                                      ) : null}
                                    </small>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() =>
                                    removeEducationFileAndDelete(index)
                                  }
                                  disabled={fileData.uploading}
                                  title="Remove file"
                                >
                                  {fileData.uploading ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : (
                                    <i className="ti ti-x"></i>
                                  )}
                                </button>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-secondary px-4"
                        onClick={() => setShowEducationModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary px-4"
                        onClick={saveEducationRecord}
                        disabled={uploadedEducationFiles.some(
                          (f) => f.uploading
                        )}
                      >
                        {uploadedEducationFiles.some((f) => f.uploading) ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <i className="ti ti-device-floppy me-1"></i>
                            Save Record
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Declaration Modal */}
            {showDeclarationModal && (
              <>
                {/* Backdrop */}
                <div
                  className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
                  style={{ zIndex: 1040 }}
                />

                {/* Declaration Modal */}
                <div
                  className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
                  style={{
                    zIndex: 1050,
                    width: "90%",
                    maxWidth: "500px",
                  }}
                >
                  {/* Modal Header */}
                  <div className="d-flex align-items-center justify-content-between p-3 border-bottom bg-light">
                    <h5 className="mb-0 text-dark">
                      Application for NOC for Outside Job
                    </h5>
                    <button
                      className="btn btn-link text-dark text-decoration-none p-0"
                      onClick={() => setShowDeclarationModal(false)}
                    >
                      <i className="ti ti-arrow-left fs-20"></i>
                    </button>
                  </div>

                  {/* Declaration Content */}
                  <div className="p-3">
                    <div className="bg-warning bg-opacity-25 p-3 rounded mb-3">
                      <h6 className="fw-bold text-dark mb-3">
                        Declaration Required
                      </h6>

                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="informationTrue"
                          checked={declarationChecks.informationTrue}
                          onChange={(e) =>
                            handleDeclarationChange(
                              "informationTrue",
                              e.target.checked
                            )
                          }
                        />
                        <label
                          className="form-check-label text-dark"
                          htmlFor="informationTrue"
                        >
                          I hereby declare that the information given above is
                          true
                        </label>
                      </div>

                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="eligibilityCriteria"
                          checked={declarationChecks.eligibilityCriteria}
                          onChange={(e) =>
                            handleDeclarationChange(
                              "eligibilityCriteria",
                              e.target.checked
                            )
                          }
                        />
                        <label
                          className="form-check-label text-dark"
                          htmlFor="eligibilityCriteria"
                        >
                          I declared that I am prima facia meeting eligibility
                          criteria (education qualification, age, experience &
                          pay-scale etc.) for the post, I intend to apply for,
                          as per the enclosed (uploaded ) advertisement
                        </label>
                      </div>

                      <div className="d-flex justify-content-center mt-4">
                        <button
                          type="button"
                          className="btn btn-secondary px-4"
                          onClick={() => {
                            handleFinalSubmit();
                          }}
                          disabled={
                            !declarationChecks.informationTrue ||
                            !declarationChecks.eligibilityCriteria ||
                            isSubmitting
                          }
                        >
                          Submit
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Probation Alert Modal */}
            {showProbationAlert && (
              <div
                className="modal d-block"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1050 }}
              >
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div
                      className="modal-header"
                      style={{
                        backgroundColor: "#4472C4",
                        color: "white",
                        borderBottom: "none",
                      }}
                    >
                      <h5 className="modal-title">
                        NOC for outside job request application
                      </h5>
                      <button
                        type="button"
                        className="btn-close btn-close-white"
                        onClick={() => setShowProbationAlert(false)}
                        aria-label="Close"
                        style={{
                          background: "none",
                          border: "none",
                          color: "white",
                          fontSize: "18px",
                          cursor: "pointer",
                          padding: "0",
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div
                      className="modal-body"
                      style={{ padding: "30px", minHeight: "150px" }}
                    >
                      <p
                        style={{ margin: "0", fontSize: "14px", color: "#333" }}
                      >
                        Cannot apply if probation is not confirmed.
                      </p>
                    </div>
                    <div
                      className="modal-footer"
                      style={{
                        borderTop: "none",
                        justifyContent: "center",
                        paddingBottom: "20px",
                      }}
                    >
                      <button
                        type="button"
                        className="btn btn-primary px-4"
                        onClick={() => setShowProbationAlert(false)}
                        style={{
                          backgroundColor: "#4472C4",
                          borderColor: "#4472C4",
                          fontSize: "12px",
                          padding: "6px 16px",
                        }}
                      >
                        OK
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Loading Modal */}
            {showLoadingModal && (
              <>
                {/* Backdrop */}
                <div
                  className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
                  style={{ zIndex: 2000 }}
                />

                {/* Loading Modal */}
                <div
                  className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
                  style={{
                    zIndex: 2001,
                    width: "90%",
                    maxWidth: "400px",
                    padding: "30px",
                  }}
                >
                  <div className="text-center">
                    {/* Spinner */}
                    <div className="mb-3">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                        style={{
                          width: "3rem",
                          height: "3rem",
                          borderWidth: "0.3em",
                        }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>

                    {/* Loading Message */}
                    <h5 className="mb-2 text-dark fw-bold">{loadingMessage}</h5>
                    <p className="text-muted mb-0">
                      Please wait while we process your request...
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Response Modal */}
            {showResponseModal && (
              <>
                {/* Backdrop */}
                <div
                  className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
                  style={{ zIndex: 2000 }}
                />

                {/* Response Modal */}
                <div
                  className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
                  style={{
                    zIndex: 2001,
                    width: "90%",
                    maxWidth: "500px",
                  }}
                >
                  {/* Modal Header */}
                  <div
                    className={`d-flex align-items-center justify-content-between p-3 border-bottom ${responseType === "success" ? "bg-success" : "bg-info"
                      }`}
                  >
                    <h5 className="mb-0 text-white fw-bold">
                      {responseType === "success" ? (
                        <>
                          <i className="ti ti-check-circle me-2"></i>
                          Success
                        </>
                      ) : (
                        <>
                          <i className="ti ti-info-circle me-2"></i>
                          Notice
                        </>
                      )}
                    </h5>
                    <button
                      className="btn btn-link text-white text-decoration-none p-0"
                      onClick={() => {
                        setShowResponseModal(false);
                        if (responseType === "success") {
                          navigate(routes.noc.outsideJob);
                        }
                      }}
                    >
                      <i className="ti ti-x fs-20"></i>
                    </button>
                  </div>

                  {/* Modal Content */}
                  <div className="p-4">
                    <div
                      className={`p-3 rounded ${responseType === "success"
                        ? "bg-success bg-opacity-10"
                        : "bg-info bg-opacity-10"
                        }`}
                    >
                      <p className="mb-0 text-dark">{responseMessage}</p>
                    </div>

                    <div className="d-flex justify-content-center mt-4">
                      <button
                        type="button"
                        className={`btn ${responseType === "success"
                          ? "btn-success"
                          : "btn-info"
                          } px-5`}
                        onClick={() => {
                          setShowResponseModal(false);
                          if (responseType === "success") {
                            navigate(routes.noc.outsideJob);
                          }
                        }}
                      >
                        OK
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            <style>
              {`
            .form-label {
              color: #333 !important;
              font-weight: 500 !important;
              margin-bottom: 8px !important;
              display: block !important;
            }
            .form-check-label {
              color: #333 !important;
              font-weight: 400 !important;
              margin-left: 5px !important;
            }
            .last\\:mb-0:last-child {
              margin-bottom: 0 !important;
            }
            .form-control[readonly] {
              background-color: #f8f9fa !important;
              cursor: not-allowed !important;
              border: 1px solid #e9ecef !important;
              color: #6c757d !important;
            }
            .form-control[readonly]:focus {
              border-color: #ced4da !important;
              box-shadow: none !important;
            }
            .animate-spin {
              animation: spin 1s linear infinite;
            }
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            /* Enhanced DatePicker styling */
            .ant-picker {
              border: 1px solid #ced4da !important;
              border-radius: 6px !important;
              transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
            }
            .ant-picker:hover {
              border-color: #80bdff !important;
            }
            .ant-picker-focused {
              border-color: #80bdff !important;
              box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
            }
            .ant-picker-input > input {
              font-size: 14px !important;
              color: #495057 !important;
            }
          `}
            </style>
          </div>
        </div>
      )}

      {/* Full Page Loader */}
      <FullPageLoader show={showLoadingModal} message={loadingMessage} />
    </>
  );
};

export default NOCOutsideJobCreateForm;
